import React, { SyntheticEvent, useCallback, useState } from 'react';
import { Button, FormField, Header, Icon, Input, Message, Modal, ModalActions, ModalContent, Popup, Select, TextArea } from 'semantic-ui-react';
import { toast } from 'react-toastify';
import { createGuarantee } from '../../../services/guarantees.service';
import SearchClientDropdown from '../../../components/SearchClientDropdown';
import DropdownOption from '../../../types/models/DropdownOption';
import { getUsers } from '../../../services/users.service';
import { USER_ROLE_ADVISOR } from '../../../utils/constants';

interface RequestData {
    cause?: string;
    type?: "ADJUSTMENT" | "GUARANTEE" | undefined;
    moneyRemaining?: string;
    clientId?: string;
    advisorId?: string;
}

const CreateAdjustmentModal = () => {

    const [openModal, setOpenModal] = useState(false);
    const [loading, setLoading] = useState(false);

    const [advisors, setAdvisors] = useState<DropdownOption[]>([]);
    const [clientIdSelected, setClientIdSelected] = useState<string>("");

    const [requestData, setRequestData] = useState<RequestData>({
        cause: "",
        type: undefined,
        clientId: "",
        advisorId: "",
        moneyRemaining: "",
    });

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (clientIdSelected === "") {
                throw new Error("Favor de seleccionar un cliente");
            }
            requestData.clientId = clientIdSelected;

            requestData.moneyRemaining = requestData.moneyRemaining?.replace(/[^0-9.,]/g, '').replace(/,/g, '');
            const response = await createGuarantee(requestData);

            if (response?.status === 201) {
                window.location.href = `/guarantees/adjustments/${response.data.id}/form`;
            } else {
                throw new Error("El ajuste no ha sido creado exitosamente, favor de contactar al area de TI");
            }
        } catch (error: any) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e: any, { name, value }: any) => {
        if (name === "type") {
            setRequestData({
                ...requestData,
                moneyRemaining: value === "GUARANTEE" ? "0" : "",
                [name]: value ?? "",
            } as RequestData);
        } else {
            setRequestData({
                ...requestData,
                [name]: value ?? "",
            } as RequestData);
        }
    };

    const handleChangeMoneyRemaining = (e: any, { name, value }: any) => {
        const newValue = value.replace(/[^0-9.,]/g, '').replace(/,/g, '');
        if (newValue.length <= 10) {
            setRequestData({
                ...requestData,
                [name]: formatMoney(newValue) ?? "",
            } as RequestData);
        }
    };

    // this method receives a string of numbers and returns a formatted string
    const formatMoney = (value: string | undefined) => {
        if (!value) {
            return "";
        }
        return value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    const fetchAdvisors = useCallback(async () => {
        try {

            const response = await getUsers(100, 0, USER_ROLE_ADVISOR);
            if (!response?.data.content) {
                return;
            }
            const advisorsList = response?.data.content.map(
                (advisor: any) => {
                    return {
                        key: advisor.id,
                        value: advisor.id,
                        text: advisor.person?.fullName ?? "Sin nombre",
                    };
                }
            );
            setAdvisors(advisorsList);
        } catch (error) {
            toast.error("Error al obtener la lista de asesores");
        }
    }, [setAdvisors]);

    const handleOpenModal = (event: SyntheticEvent) => {
        event.preventDefault();
        fetchAdvisors();
        setOpenModal(true);
    };

    const handleCancelModal = (event: SyntheticEvent) => {
        event.preventDefault();
        setOpenModal(false);
    };

    return (
        <>
            <Modal
                closeOnDimmerClick
                closeIcon={true}
                size="small"
                onClose={(event) => handleCancelModal(event)}
                open={openModal}
                trigger={
                    <Popup
                        trigger={
                            <Button icon floated='right' color="green" onClick={(event) => handleOpenModal(event)}>
                                <Icon name="plus circle" /> Crear ajuste o garantia
                            </Button>
                        }
                        content='Crear nueva garantia del tipo "Ajuste"'
                        position='bottom center'
                        size='small'
                        inverted
                    />

                } >
                <Header icon="plus circle" content="Crear nueva garantia" />
                <ModalContent>
                    <>
                        <div className="ui form">

                            <FormField
                                control={Select}
                                label={<label htmlFor="form-select-control-type">Tipo</label>}
                                options={[
                                    {
                                        key: "ADJUSTMENT",
                                        value: "ADJUSTMENT",
                                        text: "Ajuste"
                                    },
                                    {
                                        key: "GUARANTEE",
                                        value: "GUARANTEE",
                                        text: "Garantia"
                                    }
                                ]}
                                placeholder="Selecciona una opcion"
                                name="type"
                                value={requestData.type}
                                onChange={handleChange}
                                required
                                search
                                searchInput={{ id: "form-select-control-type" }}
                                clearable
                            />

                            <FormField
                                control={Input}
                                label={<label htmlFor="form-select-control-installer">Saldo por liquidar</label>}
                                placeholder="Si no hay saldo pendiente, poner 0"
                                name="moneyRemaining"
                                value={requestData.moneyRemaining}
                                onChange={handleChangeMoneyRemaining}
                                required
                                disabled={requestData.type === "GUARANTEE"}
                            />

                            <SearchClientDropdown
                                value={clientIdSelected}
                                setValue={setClientIdSelected} />

                            <FormField
                                control={Select}
                                label={<label htmlFor="form-select-control-advisor">Asesor</label>}
                                options={advisors}
                                placeholder="Selecciona una opcion"
                                name="advisorId"
                                value={requestData.advisorId}
                                onChange={handleChange}
                                required
                                search
                                searchInput={{ id: "form-select-control-advisor" }}
                                clearable
                            />

                            <FormField
                                clearable
                                control={TextArea}
                                label={<label htmlFor="form-select-control-payment">Causa de la garantia</label>}
                                placeholder="Selecciona una opcion"
                                name="cause"
                                value={requestData.cause}
                                onChange={handleChange}
                                required
                            />

                            <Message
                                info
                                icon="info circle"
                                header="Información adicional"
                                content='Si no se selecciona a un asesor, significa que el cliente se comunico directamente.'
                            />
                        </div>
                    </>
                </ModalContent>
                <ModalActions>
                    <Button icon onClick={(event) => handleCancelModal(event)} >
                        <Icon name="cancel" /> Cancelar
                    </Button>
                    <Button color="green" icon
                        loading={loading}
                        onClick={(event) => handleSubmit(event)} >
                        <Icon name="check" /> Crear ajuste
                    </Button>
                </ModalActions>
            </Modal>
        </>
    );
};

export default CreateAdjustmentModal;
